import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { RootStateOrAny } from 'react-redux';

import { makeAPIRequestReactQuery } from '../api';
import { endpoints } from '../endpoints';

import type { ClinicalNote } from './useExistingNotes';

interface StandaloneVideaNotesBatchRequest {
  standalonePatientVisitIds: string[];
}

interface StandaloneVideaNotesBatchResponse {
  patientVisits: {
    [key: string]: ClinicalNote[];
  };
}

/**
 * Fetches clinical notes for multiple standalone patient visit appointments in a single API request.
 *
 * Takes an array of standalone patient visit IDs and returns clinical notes organized by visit ID.
 * Used to efficiently load notes data for multiple visits without making individual
 * API calls for each appointment.
 *
 * @param standalonePatientVisitIds - Array of standalone patient visit IDs as strings
 * @param enabled - Whether the query should be enabled (default: true)
 * @returns Query result containing notes data organized by appointment ID
 */
export const useBatchNotesByStandalonePatientVisitIds = (
  standalonePatientVisitIds: string[],
  enabled: boolean = true,
) => {
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: RootStateOrAny) => state.login.user);

  // Create a single query for all appointment IDs
  const query = useMemo(
    () => ({
      queryKey: [
        endpoints.ambient.voiceNotes.batchNotesByStandalonePatientVisitIds,
        standalonePatientVisitIds,
      ],

      queryFn: async () => {
        if (!standalonePatientVisitIds.length) {
          return { patientVisits: {} };
        }

        const response =
          await makeAPIRequestReactQuery<StandaloneVideaNotesBatchResponse>(
            endpoints.ambient.voiceNotes.batchNotesByStandalonePatientVisitIds,
            undefined,
            'POST',
            {
              headers: {
                practice: currentPracticeId,
                org: currentOrganizationId,
              },
              data: {
                standalonePatientVisitIds: standalonePatientVisitIds,
              } as StandaloneVideaNotesBatchRequest,
            },
          );

        return response;
      },

      enabled:
        enabled &&
        !!standalonePatientVisitIds.length &&
        !!currentPracticeId &&
        !!currentOrganizationId,
      staleTime: 10 * 1000, // 10 seconds
    }),
    [
      standalonePatientVisitIds,
      currentPracticeId,
      currentOrganizationId,
      enabled,
    ],
  );

  return useQueries({ queries: [query] });
};
